import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { MerchantPanel } from '../ui/MerchantPanel';
import { SpellVendorPanel } from '../ui/SpellVendorPanel';
import { ExchangePanel } from '../ui/ExchangePanel';
import { MarketplacePanel } from '../ui/MarketplacePanel';
import type { NPCStateView, PlayerStateView } from '../../state/GameStateView';
import { GAME_FONT_FAMILY } from '@shared/constants';
import {
  ClientMessageType,
  type MerchantOpenedMessage,
  type SpellVendorOpenedMessage,
  type ExchangeOpenedMessage,
  type MarketplaceOpenedMessage,
  type MerchantBuyMessage,
  type MerchantSellMessage,
  type BuySpellMessage,
  type CloseVendorMessage,
} from '@shared/types/messages';

/**
 * Apertura/gestión de los 4 paneles de vendedor (item-merchant, spell-vendor, Exchange,
 * Marketplace): abre el panel que corresponde al mensaje del server cerrando los otros, crea los
 * paneles on-demand, botón de reparar (Maestro Herrero), auto-cierre por distancia y los send()
 * de compra/venta/cierre. Extraído de GameScene (god-object). Los paneles viven en GameScene (host)
 * porque los tocan muchos otros sistemas; se acceden vía `this.scene.*`. El estado vendor-only
 * (repairButton, throttle de auto-cierre) vive acá. Ver docs/VENDORS_AND_NPC_INTERACTION.md y
 * docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
 */
export class VendorPanelController {
  constructor(private scene: GameScene) {}

  public handleMerchantOpened(message: MerchantOpenedMessage): void {
    // Si tenía un spell vendor abierto, el server ya lo cerró al abrir éste:
    // cerrar el panel local en silencio (sin emitir CLOSE_VENDOR redundante).
    if (this.scene.spellVendorPanel?.isOpenPanel()) {
      this.scene.spellVendorPanel.close(true);
    }
    if (this.scene.exchangePanel?.isOpenPanel()) {
      this.scene.exchangePanel.close(true);
    }
    if (this.scene.marketplacePanel?.isOpenPanel()) {
      this.scene.marketplacePanel.close(true);
    }
    if (!this.scene.merchantPanel) {
      this.createMerchantPanel();
    }
    this.scene.merchantPanel?.open(message, this.scene.localGold, this.scene.lastInventorySnapshot);
    // Botón de reparar herramientas: sólo en el Maestro Herrero. Ver docs/MINING_AND_SMITHING.md.
    if (message.merchantId === 'merchant_smith_collection') {
      this.showRepairButton(message.npcId);
    } else {
      this.hideRepairButton();
    }
    // Los GameObjects del panel se crean on-demand: hay que reasignarlos a
    // la uiCamera (ignorar en cameras.main) para que no rendericen con zoom
    // y para que el hit-test del pointer caiga sobre los slots.
    this.scene.setupCameraFilters();
  }

  /** Botón flotante "Reparar herramientas" (sólo con el Maestro Herrero abierto). */
  public repairButton?: Phaser.GameObjects.Container;

  public showRepairButton(npcId: string): void {
    this.hideRepairButton();
    const { width, height } = this.scene.scale;
    const w = 240;
    const h = 34;
    const x = width / 2;
    const y = height - 60;
    const bg = this.scene.add
      .rectangle(0, 0, w, h, 0x2a1c0c, 0.96)
      .setStrokeStyle(2, 0xcaa24a, 0.9);
    const label = this.scene.add
      .text(0, 0, 'Reparar herramientas', {
        fontFamily: GAME_FONT_FAMILY,
        fontSize: '15px',
        color: '#f0d68c',
      })
      .setOrigin(0.5);
    const container = this.scene.add.container(x, y, [bg, label]);
    container.setScrollFactor(0).setDepth(10001);
    container.setSize(w, h);
    container.setInteractive(new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h), Phaser.Geom.Rectangle.Contains);
    container.on('pointerover', () => bg.setFillStyle(0x3a2810, 0.98));
    container.on('pointerout', () => bg.setFillStyle(0x2a1c0c, 0.96));
    container.on('pointerdown', () => {
      this.scene.colyseusClient?.send({ type: ClientMessageType.REPAIR_TOOLS, npcId } as never);
    });
    this.repairButton = container;
    // uiCamera: que no renderice con zoom del mundo.
    this.scene.setupCameraFilters();
  }

  public hideRepairButton(): void {
    this.repairButton?.destroy();
    this.repairButton = undefined;
  }

  public handleSpellVendorOpened(message: SpellVendorOpenedMessage): void {
    // Si tenía un item-merchant abierto, idem: cerrar silenciosamente.
    if (this.scene.merchantPanel?.isOpenPanel()) {
      this.scene.merchantPanel.close(true);
    }
    if (this.scene.exchangePanel?.isOpenPanel()) {
      this.scene.exchangePanel.close(true);
    }
    if (this.scene.marketplacePanel?.isOpenPanel()) {
      this.scene.marketplacePanel.close(true);
    }
    if (!this.scene.spellVendorPanel) {
      this.createSpellVendorPanel();
    }
    if (this.scene.spellVendorPanel?.isOpenPanel()) {
      // Re-render in place (caso refresh post-compra).
      this.scene.spellVendorPanel.setEntries(message.entries);
      this.scene.spellVendorPanel.setGold(this.scene.localGold);
      this.scene.spellVendorPanel.setInventory(this.scene.lastInventorySnapshot);
    } else {
      this.scene.spellVendorPanel?.open(message, this.scene.localGold, this.scene.lastInventorySnapshot);
    }
    this.scene.setupCameraFilters();
  }

  public handleExchangeOpened(message: ExchangeOpenedMessage): void {
    // El server cerró cualquier otro vendor al abrir éste: cerrar paneles
    // locales en silencio (sin emitir CLOSE_VENDOR redundante).
    if (this.scene.merchantPanel?.isOpenPanel()) this.scene.merchantPanel.close(true);
    if (this.scene.spellVendorPanel?.isOpenPanel()) this.scene.spellVendorPanel.close(true);
    if (this.scene.marketplacePanel?.isOpenPanel()) this.scene.marketplacePanel.close(true);
    if (!this.scene.exchangePanel) {
      this.createExchangePanel();
    }
    this.scene.exchangePanel?.open(message, this.scene.localGold, this.scene.lastInventorySnapshot);
    this.scene.setupCameraFilters();
  }

  public createExchangePanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = (savedOptions as any)?.exchangePanelX ?? width / 2 - 380;
    const panelY = (savedOptions as any)?.exchangePanelY ?? height / 2 - 240;
    this.scene.exchangePanel = new ExchangePanel(
      this.scene,
      {
        onViewItem: (itemId) => this.scene.marketNet.sendExchangeViewItem(itemId),
        onCreateOrder: (itemId, side, price, quantity) =>
          this.scene.marketNet.sendExchangeCreateOrder(itemId, side, price, quantity),
        onCancelOrder: (orderId) => this.scene.marketNet.sendExchangeCancelOrder(orderId),
        onClaimAll: () => this.scene.marketNet.sendExchangeClaimAll(),
        onAddWatch: (itemId, direction, targetPrice) => this.scene.marketNet.sendExchangeAddWatch(itemId, direction, targetPrice),
        onRemoveWatch: (watchId) => this.scene.marketNet.sendExchangeRemoveWatch(watchId),
        onAlertsSeen: () => this.scene.marketNet.sendMarketAlertsSeen(),
        onSwitchToMarketplace: () => {
          const npcId = this.scene.exchangePanel?.getCurrentNpcId();
          if (npcId) this.scene.marketNet.sendOpenMarketplace(npcId);
        },
        onClose: () => {
          const npcId = this.scene.exchangePanel?.getCurrentNpcId();
          if (npcId) this.sendCloseVendor(npcId);
        },
        onMove: (x, y) => this.scene.persistence.savePanelPosition('exchange', x, y),
      },
      { initialX: panelX, initialY: panelY }
    );
  }

  public handleMarketplaceOpened(message: MarketplaceOpenedMessage): void {
    // El server cerró cualquier otro vendor al abrir éste.
    if (this.scene.merchantPanel?.isOpenPanel()) this.scene.merchantPanel.close(true);
    if (this.scene.spellVendorPanel?.isOpenPanel()) this.scene.spellVendorPanel.close(true);
    if (this.scene.exchangePanel?.isOpenPanel()) this.scene.exchangePanel.close(true);
    if (!this.scene.marketplacePanel) {
      this.createMarketplacePanel();
    }
    this.scene.marketplacePanel?.open(message, this.scene.localGold, this.scene.lastInventorySnapshot);
    this.scene.setupCameraFilters();
  }

  public createMarketplacePanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = (savedOptions as any)?.marketplacePanelX ?? width / 2 - 430;
    const panelY = (savedOptions as any)?.marketplacePanelY ?? height / 2 - 270;
    this.scene.marketplacePanel = new MarketplacePanel(
      this.scene,
      {
        onSearch: (filters) => this.scene.marketNet.sendMarketplaceSearch(filters),
        onPublish: (slotIndex, price) => this.scene.marketNet.sendMarketplacePublish(slotIndex, price),
        onBuy: (listingId) => this.scene.marketNet.sendMarketplaceBuy(listingId),
        onCancel: (listingId) => this.scene.marketNet.sendMarketplaceCancel(listingId),
        onAppraise: (slotIndex) => this.scene.marketNet.sendMarketplaceAppraise(slotIndex),
        onSaveSearch: (name, filters, alert) => this.scene.marketNet.sendMarketplaceSaveSearch(name, filters, alert),
        onDeleteSearch: (id) => this.scene.marketNet.sendMarketplaceDeleteSearch(id),
        onFindDeals: (filters) => this.scene.marketNet.sendMarketplaceDeals(filters),
        onAlertsSeen: () => this.scene.marketNet.sendMarketAlertsSeen(),
        onRequestStats: () => this.scene.marketNet.sendRequestTraderStats(),
        onSwitchToExchange: () => {
          const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
          if (npcId) this.scene.marketNet.sendOpenExchange(npcId);
        },
        onClose: () => {
          const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
          if (npcId) this.sendCloseVendor(npcId);
        },
        onMove: (x, y) => this.scene.persistence.savePanelPosition('marketplace', x, y),
      },
      { initialX: panelX, initialY: panelY }
    );
  }

  public createMerchantPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = (savedOptions as any)?.merchantPanelX ?? width / 2;
    const panelY = (savedOptions as any)?.merchantPanelY ?? height / 2;
    this.scene.merchantPanel = new MerchantPanel(
      this.scene,
      {
        onBuy: (itemId, quantity) => this.sendMerchantBuy(itemId, quantity),
        onSell: (slotIndex, quantity) => this.sendMerchantSell(slotIndex, quantity),
        onClose: () => {
          // Avisar al server para limpiar el estado COMMERCING del NPC.
          const npcId = this.scene.merchantPanel?.getCurrentNpcId();
          if (npcId) this.sendCloseVendor(npcId);
          this.hideRepairButton();
        },
        onMove: (x, y) => this.scene.persistence.savePanelPosition('merchant', x, y),
        getNpcVisual: (npcId) => {
          const npc = this.scene.room?.state.npcs.get(npcId) as NPCStateView | undefined;
          if (!npc) return null;
          return { name: npc.name ?? '', headId: npc.headId ?? 0, bodyId: npc.bodyId ?? 0 };
        },
        getLocalPlayerVisual: () => {
          if (!this.scene.localSessionId) return null;
          const p = this.scene.room?.state.players.get(this.scene.localSessionId) as PlayerStateView | undefined;
          if (!p) return null;
          return { name: p.name ?? '', headId: p.headId ?? 0, bodyId: p.bodyId ?? 0 };
        },
      },
      {
        initialX: panelX,
        initialY: panelY,
        width: 760,
        height: 470,
      }
    );
  }

  public createSpellVendorPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = (savedOptions as any)?.spellVendorPanelX ?? width / 2;
    const panelY = (savedOptions as any)?.spellVendorPanelY ?? height / 2;
    this.scene.spellVendorPanel = new SpellVendorPanel(
      this.scene,
      {
        onBuy: (spellId) => this.sendBuySpell(spellId),
        onClose: () => {
          const npcId = this.scene.spellVendorPanel?.getCurrentNpcId();
          if (npcId) this.sendCloseVendor(npcId);
        },
        onMove: (x, y) => this.scene.persistence.savePanelPosition('spellVendor', x, y),
        onHighlightInventorySlot: (itemId) => this.flashInventorySlotForItem(itemId),
      },
      {
        initialX: panelX,
        initialY: panelY,
        width: 520,
        height: 380,
      }
    );
  }

  /**
   * Click sobre el icono de un item requerido en el SpellVendorPanel: si el
   * player tiene ese item en su inventario, buscar el slot y hacer flash en
   * el InventoryPanel. Si no lo tiene, no-op (el tooltip ya muestra "0/N").
   */
  public flashInventorySlotForItem(itemId: string): void {
    const slot = this.scene.lastInventorySnapshot.find((s) => s.itemId === itemId);
    if (!slot) return;
    this.scene.inventoryPanel?.highlightSlot(slot.slotIndex);
  }

  public sendBuySpell(spellId: string): void {
    if (!this.scene.colyseusClient || !this.scene.spellVendorPanel) return;
    const npcId = this.scene.spellVendorPanel.getCurrentNpcId();
    if (!npcId) return;
    const msg: BuySpellMessage = {
      type: ClientMessageType.BUY_SPELL,
      npcId,
      spellId,
    };
    this.scene.colyseusClient.send(msg);
  }

  public sendCloseVendor(npcId: string): void {
    if (!this.scene.colyseusClient) return;
    const msg: CloseVendorMessage = {
      type: ClientMessageType.CLOSE_VENDOR,
      npcId,
    };
    this.scene.colyseusClient.send(msg);
  }

  public sendMerchantBuy(itemId: string, quantity: number): void {
    if (!this.scene.colyseusClient || !this.scene.merchantPanel) return;
    const npcId = this.scene.merchantPanel.getCurrentNpcId();
    if (!npcId) return;
    const msg: MerchantBuyMessage = {
      type: ClientMessageType.MERCHANT_BUY,
      npcId,
      itemId,
      quantity,
    };
    this.scene.colyseusClient.send(msg);
  }

  /** Throttle del chequeo de proximidad para el cierre auto del merchant. */
  public lastMerchantCloseCheck: number = 0;

  /**
   * True si hay un panel de vendedor abierto (común o de hechizos), es decir,
   * el player está comerciando. Espeja el estado INTERACTING_NPC del server,
   * que bloquea el movimiento mientras dura la interacción.
   */
  public isVendorPanelOpen(): boolean {
    return (
      this.scene.merchantPanel?.isOpenPanel() === true ||
      this.scene.spellVendorPanel?.isOpenPanel() === true ||
      this.scene.exchangePanel?.isOpenPanel() === true ||
      this.scene.marketplacePanel?.isOpenPanel() === true
    );
  }

  /**
   * Cierra el MerchantPanel y/o SpellVendorPanel si el player se alejó más
   * de 2 tiles del NPC. El server ya rechaza buy/sell out of range; este
   * check es UX (cerrar automáticamente para no quedar con un panel "muerto").
   */
  public maybeAutoCloseMerchantPanel(): void {
    const now = this.scene.time.now;
    if (now - this.lastMerchantCloseCheck < 500) return;
    this.lastMerchantCloseCheck = now;

    if (this.scene.merchantPanel?.isOpenPanel()) {
      this.autoCloseIfFar(this.scene.merchantPanel.getCurrentNpcId(), () => this.scene.merchantPanel?.close());
    }
    if (this.scene.spellVendorPanel?.isOpenPanel()) {
      this.autoCloseIfFar(this.scene.spellVendorPanel.getCurrentNpcId(), () => this.scene.spellVendorPanel?.close());
    }
  }

  public autoCloseIfFar(npcId: string | null, closeFn: () => void): void {
    if (!npcId || !this.scene.room || !this.scene.localSessionId) {
      closeFn();
      return;
    }
    const npcState = this.scene.room.state.npcs.get(npcId) as NPCStateView | undefined;
    const playerState = this.scene.room.state.players.get(this.scene.localSessionId) as PlayerStateView | undefined;
    if (!npcState || !playerState) {
      closeFn();
      return;
    }
    const dx = Math.abs(npcState.x - playerState.x);
    const dy = Math.abs(npcState.y - playerState.y);
    if (Math.max(dx, dy) > 2) closeFn();
  }

  public sendMerchantSell(slotIndex: number, quantity: number): void {
    if (!this.scene.colyseusClient || !this.scene.merchantPanel) return;
    const npcId = this.scene.merchantPanel.getCurrentNpcId();
    if (!npcId) return;
    const msg: MerchantSellMessage = {
      type: ClientMessageType.MERCHANT_SELL,
      npcId,
      slotIndex,
      quantity,
    };
    this.scene.colyseusClient.send(msg);
  }
}
